import React, { useEffect, useState } from "react";
import { Form, Input, Radio, Checkbox, message } from "antd";
import FormModal from "../../../components/FormModal";
import PasswordFields from "../../../components/PasswordFields";
import { extractMsg } from "../../../utils/errorUtils";

export function AddUserDialog({ open, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave({
        name: values.name,
        password: values.password,
        isAdmin: values.userRole === "Administrator",
        authorities:
          values.userRole === "Administrator" ? ["ROLE_ADMIN"] : ["ALL_USERS"],
      });
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to add user"));
      console.error("Failed to add user:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title="Add New User"
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
      submitText="Add"
      initialValues={{ userRole: "User" }}
    >
      <Form.Item name="name" label="User Name" rules={[{ required: true }]}>
        <Input placeholder="Enter user name" />
      </Form.Item>
      <PasswordFields />
      <Form.Item name="userRole" label="User Role" rules={[{ required: true }]}>
        <Radio.Group>
          <Radio value="User">User</Radio>
          <Radio value="Administrator">Administrator</Radio>
        </Radio.Group>
      </Form.Item>
    </FormModal>
  );
}

export function ChangePasswordDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const newPasswordValue = Form.useWatch("newPassword", form);
  const confirmPasswordValue = Form.useWatch("confirmPassword", form);

  const isPasswordValid = Boolean(newPasswordValue);
  const isConfirmPasswordValid =
    Boolean(confirmPasswordValue) && newPasswordValue === confirmPasswordValue;

  useEffect(() => {
    if (open) form.resetFields();
  }, [open, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      await onSave(user.name, values.newPassword, values.confirmPassword);
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to change password"));
      console.error("Failed to change password:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title={`Change Password: ${user?.name}`}
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item
        name="newPassword"
        label="New Password"
        validateStatus={isPasswordValid ? "success" : undefined}
        rules={[{ required: true, message: "Please enter a new password" }]}
      >
        <Input.Password placeholder="New password" />
      </Form.Item>
      <Form.Item
        name="confirmPassword"
        label="Confirm Password"
        dependencies={["newPassword"]}
        validateStatus={isConfirmPasswordValid ? "success" : undefined}
        extra={
          isConfirmPasswordValid ? (
            <span style={{ color: "#52c41a" }}>Passwords match</span>
          ) : null
        }
        rules={[
          { required: true, message: "Please confirm your password" },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue("newPassword") === value)
                return Promise.resolve();
              return Promise.reject(new Error("Passwords do not match"));
            },
          }),
        ]}
      >
        <Input.Password placeholder="Confirm new password" />
      </Form.Item>
    </FormModal>
  );
}

const AUTHORITIES = [
  { key: "ALL_USERS", label: "All Users" },
  { key: "ROLE_ADMIN", label: "Admin" },
  { key: "ROLE_ANALYST", label: "Analyst" },
  { key: "ROLE_MODELER", label: "Modeler" },
];

export function EditAuthoritiesDialog({ open, user, onClose, onSave }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      form.setFieldsValue({
        authorities: (user.groups || []).map((g) => {
          if (g === "all_users") return "ALL_USERS";
          return `ROLE_${g.toUpperCase()}`;
        }),
      });
    }
  }, [open, user, form]);

  const handleFinish = async (values) => {
    setLoading(true);
    try {
      const selected = values.authorities || [];
      await onSave(user.name, {
        name: user.name,
        isAdmin: selected.includes("ROLE_ADMIN"),
        authorities: selected,
      });
      onClose();
    } catch (error) {
      message.error(extractMsg(error, "Failed to update authorities"));
      console.error("Failed to update authorities:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      title={`Edit Authorities: ${user?.name}`}
      open={open}
      onClose={onClose}
      onFinish={handleFinish}
      loading={loading}
      form={form}
    >
      <Form.Item name="authorities" label="Authorities">
        <Checkbox.Group>
          {AUTHORITIES.map((auth) => (
            <Checkbox key={auth.key} value={auth.key}>
              {auth.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </FormModal>
  );
}
